import type { DtoolsClassifiableLine } from "./dtoolsComponentClassification";
import { classifyDtoolsComponentTier } from "./dtoolsComponentClassification";
import type { DtoolsStagingLine } from "./dtoolsJsonStaging";

export type DtoolsEquipmentRole =
  | "Projection"
  | "Video Processing"
  | "Audio Processing"
  | "Amplification"
  | "Front Speakers"
  | "Surround Speakers"
  | "Height Speakers"
  | "Subwoofers"
  | "Media Source"
  | "Automation"
  | "Networking"
  | "Power"
  | "Rack"
  | "Supporting Components"
  | "Services"
  | "Display"
  | "Screen"
  | "Control"
  | "Integration";

type RolePattern = { role: DtoolsEquipmentRole; pattern: RegExp };

const ROLE_PATTERNS: RolePattern[] = [
  { role: "Projection", pattern: /\b(projector|laser projection|projection system)\b/i },
  { role: "Video Processing", pattern: /\b(video processor|madvr|lumagen|scaler|video scaling|hdr processing)\b/i },
  { role: "Audio Processing", pattern: /\b(surround processor|immersive processor|processor|trinnov|altitude|stormaudio|dirac live|room optimisation|room optimization|dsp\b|pre-?pro)\b/i },
  { role: "Amplification", pattern: /\b(amplifier|amp\b|power amp|monoblock|multi-?channel amp)\b/i },
  { role: "Subwoofers", pattern: /\b(subwoofer|\bsub\b|bass module|lf driver)\b/i },
  { role: "Height Speakers", pattern: /\b(height speaker|atmos speaker|elevation speaker|overhead speaker|height channel)\b/i },
  { role: "Surround Speakers", pattern: /\b(surround speaker|rear speaker|side surround|dipole|bipole surround)\b/i },
  { role: "Front Speakers", pattern: /\b(front speaker|lcr|centre channel|center channel|tower speaker|floorstander)\b/i },
  { role: "Media Source", pattern: /\b(kaleidescape|blu-?ray|streamer|media player|apple tv|zidoo|oppo|source device)\b/i },
  { role: "Automation", pattern: /\b(automation|control4|crestron|lutron|savant|rti\b|keypad|touch panel)\b/i },
  { role: "Networking", pattern: /\b(network switch|router|access point|wifi|ethernet|network core|unifi|aruba)\b/i },
  { role: "Power", pattern: /\b(power conditioner|ups\b|power distribution|power strip|isolation transformer)\b/i },
  { role: "Rack", pattern: /\b(rack\b|cabinet|equipment rack|middle atlantic|chief rack)\b/i },
  { role: "Screen", pattern: /\b(projection screen|acoustically transparent screen|fixed screen|motorised screen|motorized screen)\b/i },
  { role: "Display", pattern: /\b(display|television|\btv\b|oled panel|microled)\b/i },
  { role: "Control", pattern: /\b(remote control|handheld remote|ir repeater)\b/i },
  { role: "Integration", pattern: /\b(programming|commissioning|calibration|system integration|installation labour)\b/i },
  { role: "Services", pattern: /\b(labour|labor|service|programming fee|design fee|project management)\b/i },
];

function lineHaystack(line: DtoolsClassifiableLine): string {
  return [
    line.category,
    line.subcategory,
    line.manufacturer,
    line.model,
    line.polishedDescription,
    line.dtoolsDescription,
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

/** Infer the functional role of quoted equipment for AI briefs and customer copy. */
export function inferDtoolsEquipmentRole(line: DtoolsClassifiableLine): DtoolsEquipmentRole {
  if (line.bucket === "service") return "Services";
  if (classifyDtoolsComponentTier(line) === "SUPPORTING_COMPONENT") return "Supporting Components";

  const haystack = lineHaystack(line);
  for (const { role, pattern } of ROLE_PATTERNS) {
    if (pattern.test(haystack)) return role;
  }

  if (/\b(speaker|loudspeaker)\b/i.test(haystack)) return "Surround Speakers";
  if (/\b(av receiver|receiver|avr)\b/i.test(haystack)) return "Audio Processing";

  return "Integration";
}

/** Customer-facing benefit label — prefer capability over raw model name. */
export function inferDtoolsEquipmentBenefit(line: DtoolsStagingLine): string {
  const role = inferDtoolsEquipmentRole(line);
  const haystack = lineHaystack(line);

  if (role === "Projection" && /\b(laser|4k|8k)\b/i.test(haystack)) {
    if (/\b8k\b/i.test(haystack)) return "Native 8K Laser Projection";
    return "Native 4K Laser Projection";
  }
  if (role === "Audio Processing" && /\b(atmos|immersive|7\.1|9\.|11\.|13\.|15\.|17\.|21\.)/i.test(haystack)) {
    const match = haystack.match(/\b(\d+\.\d+\.\d+|\d+\.\d+)\b/);
    if (match) return `Dolby Atmos ${match[1]} Immersive Audio`;
    return "Dolby Atmos Immersive Audio";
  }
  if (role === "Audio Processing" && /\btrinnov\b/i.test(haystack)) return "Trinnov Room Optimisation";
  if (role === "Media Source" && /\bkaleidescape\b/i.test(haystack)) return "Kaleidescape Reference Movie Playback";
  if (role === "Video Processing") return "Dynamic HDR Video Processing";
  if (role === "Subwoofers") return "Controlled Low-Frequency Impact";
  if (role === "Front Speakers" || role === "Surround Speakers" || role === "Height Speakers") {
    const brand = String(line.manufacturer ?? "").trim();
    if (brand && /\b(perlisten|sonance|bowers|kef|monitor audio|rbh|jbl synthesis)\b/i.test(brand)) {
      return "Reference Loudspeaker System";
    }
    return role;
  }
  if (role === "Amplification") return "High-Current Power Amplification";
  if (role === "Automation") {
    if (/\b(lutron|homeworks|radio ra|ketra|lighting)\b/i.test(haystack)) {
      return "Whole-Home Lighting Control";
    }
    return "Unified Home Control";
  }
  if (role === "Networking") return "Reliable AV Networking";
  if (role === "Screen") return "Cinema-Grade Projection Screen";
  if (role === "Display") return "Premium Display Performance";
  if (role === "Integration") return "Professional System Integration";
  if (role === "Services") return "Expert Installation & Commissioning";
  if (role === "Supporting Components") {
    const brandModel = [line.manufacturer, line.model].filter(Boolean).join(" ").trim();
    return brandModel || "";
  }

  const brandModel = [line.manufacturer, line.model].filter(Boolean).join(" ").trim();
  if (brandModel) return brandModel;

  return "";
}

/** One-sentence customer benefit for PDF “Why it matters” blocks. */
export function inferDtoolsEquipmentWhyItMatters(line: DtoolsStagingLine): string | null {
  const role = inferDtoolsEquipmentRole(line);
  const haystack = lineHaystack(line);

  const byRole: Partial<Record<DtoolsEquipmentRole, string>> = {
    Subwoofers: "Adds depth and impact to movies and music.",
    Amplification: "Powers multiple listening zones while maintaining clarity and control.",
    Automation: "Provides simple everyday control of the home.",
    "Audio Processing": "Shapes surround sound so films and music feel more immersive.",
    Projection: "Brings a true cinema-scale image into the viewing space.",
    "Video Processing": "Keeps motion smooth and colour accurate for a more cinematic picture.",
    "Media Source": "Delivers reference-quality movie playback without streaming limitations.",
    "Front Speakers": "Anchors dialogue and front-stage detail you notice every day.",
    "Surround Speakers": "Wraps sound around the room for a more engaging experience.",
    "Height Speakers": "Adds overhead effects that make cinema sound more realistic.",
    Display: "Creates a premium focal point for everyday entertainment.",
    Screen: "Provides the dedicated canvas needed for a convincing cinema image.",
    Networking: "Keeps entertainment and smart devices connected reliably.",
    Control: "Makes everyday control intuitive for the whole household.",
    Integration: "Ensures everything is installed, tested, and ready to use.",
    Services: "Delivers professional installation, testing, and handover.",
  };

  if (byRole[role]) return byRole[role]!;

  if (/\b(touch\s*screen|touch\s*panel|keypad)\b/i.test(haystack)) {
    return "Provides simple everyday control of the home.";
  }
  if (/\b(speaker|loudspeaker)\b/i.test(haystack)) {
    return "Delivers the clarity and presence expected from a premium listening space.";
  }

  return null;
}

export function formatEquipmentRoleBriefLine(line: DtoolsStagingLine): string {
  const role = inferDtoolsEquipmentRole(line);
  const brandModel = [line.manufacturer, line.model].filter(Boolean).join(" ").trim();
  const qty = line.quantity > 1 ? ` ×${line.quantity}` : "";
  if (brandModel) return `${role}: ${brandModel}${qty}`;
  const label = String(line.polishedDescription ?? line.dtoolsDescription ?? "").trim() || role;
  return `${role}: ${label}${qty}`;
}
